import React from 'react'
import styled, { css } from 'styled-components'
import cn from 'classnames'
import { FormCustom, FormCustomProps } from '@bootstrap-styled/v4'
import { ifElse } from '@bootstrap-styled/css-mixins'

export type RadioBoxProps = {
    className?: string
    name?: string
    id?: string | number
    radio?: Required<FormCustomProps['radio']>
    content?: boolean
    checkType?: 'default' | 'radiobox'
} & Omit<FormCustomProps, 'radio'>

const RadioBoxUnstyled = React.memo<RadioBoxProps>(
    ({
        name = '',
        id,
        radio: radioSrc,
        value,
        checkType = 'default',
        className,
        children,
        ...props
    }) => {
        const radio = radioSrc || {
            name,
            id
        }
        const classes = cn(className, {
            [`custom-${checkType}`]: !!checkType
        })
        return (
            <FormCustom className={classes} radio={radio} value={value || id} {...props}>
                {children}
            </FormCustom>
        )
    },
    (prevProps, nextProps) => {
        return (
            prevProps.children === nextProps.children &&
            prevProps.checked === nextProps.checked &&
            prevProps.disabled === nextProps.disabled
        )
    }
)

const RadioBox = styled(RadioBoxUnstyled)`
    &&.custom-radio {
        color: ${props => props.theme['$custom-control-label-color']};
        height: ${props => props.theme['$custom-radiobox-size']};
        min-width: ${props => props.theme['$custom-radiobox-size']};
        line-height: ${props => props.theme['$custom-radiobox-size']};
        text-align: center;
        padding: 0;

        /* radius style */
        & .custom-control-indicator {
            pointer-events: none;
            user-select: none;
            box-shadow: ${props => props.theme['$custom-control-indicator-box-shadow']};

            display: block;
            position: absolute;
            top: 0px;
            left: 0px;
            width: 100%;
            height: 100%;
            border: ${props => props.theme['$custom-control-indicator-border-width']} solid
                ${props => props.theme['$custom-control-indicator-border-color']};
            background-color: ${props => props.theme['$custom-control-indicator-bg']};
            transition: ${props => props.theme['$custom-forms-transition']};
            ${props =>
                ifElse(
                    props.theme['$enable-rounded'],
                    css`
                        border-radius: ${props.theme['$custom-checkbox-indicator-border-radius']};
                    `,
                    css`
                        border-radius: 0;
                    `
                )}
        }

        /* Styles base by radio state control */
        & .custom-control-input {
            &:checked {
                & ~ .custom-control-indicator {
                    background-color: ${props => props.theme['$custom-control-indicator-bg']};
                    border-color: ${props =>
                        props.theme['$custom-control-indicator-checked-border-color']};
                }

                & ~ .custom-control-description {
                    color: ${props => props.theme['$custom-radiobox-checked-color']};
                }
            }

            &:focus {
                & ~ .custom-control-indicator {
                    box-shadow: none;
                }
            }
        }

        & .custom-control-description {
            position: relative;
            width: 100%;
        }
    }
    &&.custom-radiobox {
        color: ${props => props.theme['$custom-control-label-color']};
        height: auto;
        text-align: center;
        padding: 0;
        line-height: ${props => props.theme['$custom-checkbutton-indicator-size']};
        padding: ${props =>
            `${props.theme['$custom-checkbutton-spacer-y']} ${props.theme['$custom-checkbutton-spacer-x']}`};

        /* checkbox style */
        & .custom-control-indicator {
            pointer-events: none;
            user-select: none;
            position: relative;
            align-items: center;
            justify-content: center;
            display: flex;
            top: 0px;
            left: 0px;
            min-width: 21px;
            max-width: 21px;
            min-height: 21px;
            max-height: 21px;

            opacity: 1;
            border: ${props => props.theme['$custom-control-indicator-border-width']} solid
                ${props => props.theme['$custom-control-indicator-border-color']};
            background-color: ${props => props.theme['$custom-control-indicator-bg']};
            &:before {
                content: '';
                position: absolute;
            }

            ${props =>
                ifElse(
                    css`
                        border-radius: ${props.theme['$custom-checkbox-indicator-border-radius']};
                    `,
                    css`
                        border-radius: 50%;
                    `
                )}
        }

        /* Styles base at checkbox state control */
        & .custom-control-input {
            &:checked {
                & ~ .custom-control-indicator {
                    background-color: ${props => props.theme['$custom-control-indicator-bg']};
                    border-color: ${props =>
                        props.theme['$custom-control-indicator-checked-border-color']};
                    &:before {
                        /* max-width: 10.5px;
                        max-height: 10.5px;
                        min-width: 10.5px;
                        min-height: 10.5px; */
                        width: 11px;
                        height: 11px;
                        border-radius: 50%;

                        background-color: ${props =>
                            props.theme['$custom-control-indicator-checked-bg']};
                    }
                }
                & ~ .custom-control-description {
                    color: ${props => props.theme['$custom-control-label-color']};
                }
            }

            &:focus {
                & ~ .custom-control-indicator {
                    box-shadow: none;
                }
            }
            & .custom-control-description {
                margin-top: 2.1px;
                position: relative;
                width: 100%;
            }
        }

        /* Styles for checkbox text label */
        & .custom-control-description {
            margin-top: 2.1px;
            display: inline-block;
            position: relative;
            margin-left: 10px;
            min-height: auto;
        }
    }
`

export default RadioBox
